import type { Flow } from "@learnengine/shared-types";

export interface TimelineEvents {
  onPlay?: () => void;
  onPause?: () => void;
  onTick?: (currentTimeSeconds: number) => void;
  onComplete?: () => void;
}

export class TimelineEngine {
  private flow: Flow | null = null;
  private currentTimeSeconds = 0;
  private isPlaying = false;
  private lastFrameTime: number | null = null;
  private rafId: number | null = null;

  constructor(private readonly events: TimelineEvents = {}) {}

  loadFlow(flow: Flow) {
    this.stop();
    this.flow = flow;
    this.currentTimeSeconds = 0;
  }

  play() {
    if (!this.flow || this.isPlaying) return;
    this.isPlaying = true;
    this.lastFrameTime = performance.now();
    this.events.onPlay?.();
    this.rafId = requestAnimationFrame(this.tick);
  }

  pause() {
    if (!this.isPlaying) return;
    this.isPlaying = false;
    this.lastFrameTime = null;
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    this.events.onPause?.();
  }

  seek(seconds: number) {
    if (!this.flow) return;
    const totalDuration = this.flow.nodes.reduce((sum, n) => sum + n.durationSeconds, 0);
    this.currentTimeSeconds = Math.max(0, Math.min(seconds, totalDuration));
    this.events.onTick?.(this.currentTimeSeconds);
  }

  stop() {
    this.pause();
    this.currentTimeSeconds = 0;
  }

  private tick = (now: number) => {
    if (!this.isPlaying || !this.flow) return;

    if (this.lastFrameTime == null) {
      this.lastFrameTime = now;
    }

    const deltaSeconds = (now - this.lastFrameTime) / 1000;
    this.lastFrameTime = now;

    this.currentTimeSeconds += Math.max(0, deltaSeconds);

    const totalDuration = this.flow.nodes.reduce((sum, n) => sum + n.durationSeconds, 0);

    if (this.currentTimeSeconds >= totalDuration) {
      this.currentTimeSeconds = totalDuration;
      this.events.onTick?.(this.currentTimeSeconds);
      this.events.onComplete?.();
      this.pause();
      return;
    }

    this.events.onTick?.(this.currentTimeSeconds);
    this.rafId = requestAnimationFrame(this.tick);
  };
}
